import React, { useState, useContext, useEffect, useCallback } from "react";
import { AccountContext } from "./../../store/AccountContext";
import { OverviewContext } from "./../../store/OverviewContext";
import {
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link } from "react-router-dom";
import usePromise from "react-use-promise";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import visa from "payment-icons/min/flat/visa.svg";
import mastercard from "payment-icons/min/flat/mastercard.svg";
import amex from "payment-icons/min/flat/amex.svg";
import discover from "payment-icons/min/flat/discover.svg";
import { useToasts } from "react-toast-notifications";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useStripe } from "@stripe/react-stripe-js";
import { CurrencySymbol } from "@booyaltd/core";

/**
 * PM5 T2 (money path, batch "myaccount-membership") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ (`@mui/material`) `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * PAYMENTS + SUBSCRIPTIONS ESCALATION CLASS — monthly→annual plan upgrade,
 * including the SCA `confirmCardPayment` poll. Styling-only: no handler,
 * condition, state value, prop or DOM node moved. No Stripe Elements are
 * mounted here (the saved card is billed), so there is no Elements styling
 * object in this file.
 *
 * DEAD RULES DROPPED (guide §1.2 row 1) — declared but never referenced from
 * this file's JSX: `myAccountContainer`, `myAccountGridContainer`,
 * `myAccountTitle`, `historyContainer`, `membershipOptionsContainer`,
 * `membershipPlanContainer`, `productDetails`, `closeDialogButton` and
 * `responseHelp`. The terms `<Box className={classes.termsContainer}>`
 * referenced a key that does not exist in this file's `useStyles` object, so
 * it resolved to `className={undefined}` and applied nothing.
 */

/* ---------------------------------------------------------------------- *
 * styled() — non-MUI elements that carry a fixed style
 * ---------------------------------------------------------------------- */

// was: useStyles().background — a bare <div>.
const Background = styled("div")({
  flexGrow: 1,
  backgroundColor: "#e8e8e8",
});

// was: useStyles().activeBreadcrumb — a react-router <Link>.
const ActiveBreadcrumbLink = styled(Link)(({ theme }) => ({
  color: theme.palette.background.ffNavy,
  "&:hover": {
    color: theme.palette.background.ffGreen,
  },
}));

// was: useStyles().removeUnderline — a react-router <Link>.
const NoUnderlineLink = styled(Link)({
  textDecoration: "none",
});

// was: useStyles().defaultPaymentMethodCardBrand — a bare <img>.
const CardBrandImg = styled("img")(({ theme }) => ({
  width: "50px",
  maxWidth: "50px",
  paddingRight: theme.spacing(2.5),
}));

// was: useStyles().cardExpiry — a bare <span>. The `!important` is carried
// across verbatim.
const CardExpirySpan = styled("span")({
  fontSize: "0.85rem",
  fontWeight: "400 !important",
});

/* ---------------------------------------------------------------------- *
 * sx — styles on MUI components
 * ---------------------------------------------------------------------- */

/**
 * ♦ NEW DELTA (guide §2). v4's `.MuiGrid-container` declared `width: 100%`;
 * v9's Grid container has NO width rule at all — `generateGridStyles` emits
 * only `display: flex` / `flex-wrap` / `gap`. Harmless for a block-level
 * container, but a container nested DIRECTLY inside another container is a
 * flex item, and `width: auto` makes it shrink-to-fit. Measured in Chromium
 * on the exact emitted CSS: 800px in v4, 32px in v9, restored to 800px by
 * this rule. Restated verbatim on every converted container.
 */
const v4GridContainerWidthSx = { width: "100%" };

/**
 * ♦ A `<Grid item>` whose parent is another Grid ITEM, not a Grid container —
 * v9 publishes `--Grid-parent-columns` only from a container's `> *` rule, so
 * the `size` `calc()` would be invalid here. Guide §2 ("Grid without a
 * container"): port it as a Box carrying the literal v4 declarations. The one
 * orphan in this file is `xs/sm/md = 12`, i.e. 100% at every breakpoint.
 */
const orphanGridItemFullSx = {
  boxSizing: "border-box",
  flexGrow: 0,
  flexBasis: "100%",
  maxWidth: "100%",
};

// was: useStyles().breadcrumbContainer
const breadcrumbContainerSx = (theme) => ({
  margin: theme.spacing(1, 0, 2, 0),
});

// was: useStyles().disabledBreadcrumb
const disabledBreadcrumbSx = { color: "#777777" };

// was: useStyles().myAccountSubTitle. The literal `@media (min-width:600px)` /
// `(min-width:960px)` become `breakpoints.up("sm"|"md")`; `themeV5` pins those
// to the v4 values (600/960), so the emitted query is identical.
const myAccountSubTitleSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "1.4rem",
  textAlign: "center",
  // marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.4rem",
    lineHeight: "1.4rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "1.6rem",
    lineHeight: "1.8rem",
  },
});

// was: useStyles().myAccountThirdTitle
const myAccountThirdTitleSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "1.2rem",
  textAlign: "center",
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.2rem",
    lineHeight: "1.4rem",
  },
  [theme.breakpoints.up("md")]: {
    marginTop: theme.spacing(3),
    fontSize: "1.4rem",
    lineHeight: "1.6rem",
  },
});

// was: useStyles().bodyText
const bodyTextSx = (theme) => ({
  marginBottom: theme.spacing(2),
  color: theme.palette.background.ffNavy,
  "& span": {
    color: "#676767",
    textDecoration: "underline",
    "&:hover": {
      cursor: "pointer",
    },
  },
});

// was: useStyles().bodyTextSml
const bodyTextSmlSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "0.9rem",
  "& span": {
    fontWeight: "500",
  },
});

// was: useStyles().currentPlanLabel
const currentPlanLabelSx = (theme) => ({
  padding: theme.spacing(0.5),
  backgroundColor: theme.palette.background.ffGreen,
  fontWeight: 600,
  color: "#fff",
  textAlign: "center",
  borderRadius: "10px 10px 0 0",
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(1),
  },
});

// was: useStyles().promoLabel
const promoLabelSx = (theme) => ({
  padding: theme.spacing(0.5),
  backgroundColor: theme.palette.background.upgrade,
  fontWeight: 600,
  color: "#fff",
  textAlign: "center",
  borderRadius: "10px 10px 0 0",
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(1),
  },
});

// was: useStyles().membershipPlanContainerInnerDisabled
const membershipPlanContainerInnerDisabledSx = (theme) => ({
  display: "flex",
  alignItems: "center",
  flexDirection: "column",
  justifyContent: "center",
  backgroundColor: "#fafafa",
  minHeight: "100px",
  padding: theme.spacing(2, 3),
  border: "2px solid #fafafa",
  textAlign: "center",
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(3, 4),
  },
});

// was: useStyles().membershipPlanContainerMid
const membershipPlanContainerMidSx = (theme) => ({
  display: "flex",
  alignItems: "center",
  flexDirection: "column",
  justifyContent: "center",
  padding: theme.spacing(3, 1),
  minHeight: "100px",
  textAlign: "center",
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(5, 4),
  },
});

// was: useStyles().membershipPlanContainerInner
const membershipPlanContainerInnerSx = (theme) => ({
  display: "flex",
  alignItems: "center",
  flexDirection: "column",
  justifyContent: "center",
  backgroundColor: "#fff",
  minHeight: "100px",
  padding: theme.spacing(2, 3),
  border: "2px solid #fff",
  textAlign: "center",
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(3, 4),
  },
});

// was: useStyles().productTitle
const productTitleSx = (theme) => ({
  fontWeight: 500,
  fontSize: "1.1rem",
  color: theme.palette.background.ffNavy,
  alignContent: "center",
  alignItems: "center",
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.2rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "1.4rem",
  },
});

// was: useStyles().productCost
const productCostSx = (theme) => ({
  fontWeight: 600,
  fontSize: "1.2rem",
  lineHeight: "1.2rem",
  color: theme.palette.background.ffNavy,
  alignItems: "center",
  // PARITY RESTORE — the responsive `& span` rules are nested INSIDE the
  // `& span` block rather than left in the `sm`/`md` blocks where v4 declared
  // them. MUI's `styleFunctionSx` seeds its output with an empty scaffold of the
  // theme's own breakpoint keys, so breakpoint blocks are emitted FIRST and a
  // plain nested selector written at the top level of an `sx` object comes out
  // after `@media { & span }`; at equal (0,1,1) specificity the later rule wins,
  // so the `1rem` xs size beat `0.9rem` at every width. Nesting the breakpoints
  // under the selector emits v4's three rules in v4's order. Same fault measured
  // on `FreeTrial/Top.tsx`'s hero subheading (48px where v4 rendered 64px).
  "& span": {
    fontSize: "1rem",
    lineHeight: "1rem",
    fontWeight: "normal",
    [theme.breakpoints.up("sm")]: {
      fontSize: "0.9rem",
    },
    [theme.breakpoints.up("md")]: {
      fontSize: "0.9rem",
    },
  },
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.1rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "1.2rem",
  },
});

// was: useStyles().cardContainer
const cardContainerSx = (theme) => ({
  background: "#ddd",
  display: "flex",
  padding: theme.spacing(1.5, 2),
});

// was: useStyles().divider
const dividerSx = (theme) => ({
  height: "1px",
  margin: theme.spacing(2, 0, 2, 0),
  backgroundColor: "#ddd",
});

// was: useStyles().confirmContainer
const confirmContainerSx = (theme) => ({
  backgroundColor: "#ddd",
  padding: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(2, 1.5),
  },
});

// was: useStyles().termsLabel
const termsLabelSx = (theme) => ({
  color: theme.palette.background.ffNavyLighter,
  fontSize: "0.8rem",
  lineHeight: "1rem",
  textAlign: "left",
  [theme.breakpoints.up("sm")]: {
    fontSize: "0.8rem",
    lineHeight: "1rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "0.8rem",
    lineHeight: "1rem",
  },
  "& a": {
    color: theme.palette.background.ffNavyLighter,
    fontWeight: 500,
  },
});

// was: useStyles().responseBox
const responseBoxSx = {
  textAlign: "center",
  padding: "10%",
};

// was: useStyles().responseIcon
const responseIconSx = (theme) => ({
  width: "4rem",
  height: "4rem",
  [theme.breakpoints.up("sm")]: {
    width: "5rem",
    height: "5rem",
  },
});

// PARITY RESTORE (guide §2, "Button default color"): every <Button> in this
// file carried NO v4 class. v4's plain <Button> defaulted to `color="default"`,
// whose root style was `color: theme.palette.text.primary`. v5+ deleted
// `default` and made `primary` the default — and this theme's `primary.main` IS
// the lime that `typography.button` paints the button background with, so an
// uncompensated port renders invisible lime-on-lime "Upgrade to Annual" /
// "My Account" / "Watch Now" buttons.
const buttonColourRestoreSx = { color: "text.primary" };

export default function PaymentHistory() {
  const stripe = useStripe();
  const { addToast } = useToasts();
  const [currency, setCurrency] = useState();

  // Account Context
  const {
    getMembershipDetails,
    getUpgradePreview,
    upgradePreviewDetails,
    updating,
    handleUpgradeMembership,
    resetErrors,
    subscriptionProducts,
    getSubscriptionProducts,
  } = useContext(AccountContext);

  const { overview } = useContext(OverviewContext);

  useEffect(() => {
    getSubscriptionProducts();
    return () => resetErrors();
    // eslint-disable-next-line
  }, []);

  const standardProduct =
    subscriptionProducts &&
    subscriptionProducts?.results.find((p) => p.memberStatusId === "standard");

  console.log(standardProduct);

  const [reloadTrigger, setReloadTrigger] = useState(Date.now());
  const [membershipDetails, , state] = usePromise(getMembershipDetails, [
    reloadTrigger,
  ]);

  const memberShipDetailsLoading = state === "pending";

  useEffect(() => {
    if (membershipDetails && membershipDetails.currency) {
      setCurrency(membershipDetails.currency.toUpperCase());
    } else {
      setCurrency("GBP");
    }
  }, [membershipDetails]);

  const [submittingButton, setSubmittingButton] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [polling, setPolling] = useState(false);
  // const [annualUpgradeSubmitted, setAnnualUpgradeSubmitted] = useState(false);
  const [annualUpgradeSuccess, setAnnualUpgradeSuccess] = useState(false);
  const [annualUpgradeFailure, setAnnualUpgradeFailure] = useState(false);

  // eslint-disable-next-line
  const reload = () => setReloadTrigger(Date.now());

  // Handle Upgrade Membership
  const upgradeMembership = useCallback(async () => {
    try {
      setPolling(true);
      handleUpgradeMembership();

      setTimeout(statusPoller, 5000);
    } catch (e) {
      addToast(e.toString(), {
        appearance: "error",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addToast, handleUpgradeMembership]);

  const statusPoller = async () => {
    // Documentation
    // https://stripe.com/docs/billing/subscriptions/fixed-price#manage-payment-authentication

    setPolling(true);
    setReloadTrigger(Date.now());

    console.log("Starting poll...");

    // Get the Latest Payment
    const latestPayment = async () => {
      const memberDetails = await getMembershipDetails();
      const paymentDetails = memberDetails.payments[0];
      return paymentDetails;
    };

    const LP = await latestPayment();

    // If the latest payment entry is failure
    if (LP.status === "failure") {
      const latestInvoice = JSON.parse(LP.paymentProviderRawPayload);

      console.log("in fail");
      // Reason is 'Action Required', then the payment requires action
      if (latestInvoice.payment_intent.status === "requires_action") {
        console.log("Action Required...");

        const clientSecret = latestInvoice.payment_intent.client_secret;
        const paymentMethod = latestInvoice.payment_intent.payment_method;

        // Pass client_ secret and payment_intent to confirmCardPayment (SCA Modal)
        return stripe
          .confirmCardPayment(clientSecret, {
            payment_method: paymentMethod,
          })
          .then((result) => {
            if (result.error) {
              console.log("Payment Auth failure...", result);
              setPolling(false);
              setAnnualUpgradeFailure(true);

              return;
            } else {
              if (result.paymentIntent.status === "succeeded") {
                console.log("Payment Auth success...");
                setPolling(false);
                setAnnualUpgradeSuccess(true);
                return;
              }
            }
          })
          .catch((error) => {
            setPolling(false);
            setAnnualUpgradeFailure(true);
            return;
          });
      }

      console.log("Success...");

      setPolling(false);
      setAnnualUpgradeFailure(true);
      return;
    }
    // If the latest payment entry is success, show success message
    else if (LP.status === "success") {
      console.log("Payment Success - No Auth Required.");
      setPolling(false);
      setAnnualUpgradeSuccess(true);
      return;
    }
  };

  const handleTermsChecked = (event) => {
    setTermsChecked(event.target.checked);
    if (termsChecked) {
      setSubmittingButton(false);
    } else {
      setSubmittingButton(true);
    }
    // setSubmittingButton(true);
  };

  useEffect(() => {
    getUpgradePreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Renewal Date config
  let renewalDate = new Date();

  renewalDate = new Intl.DateTimeFormat("en-GB", {
    year: "numeric",
    month: "long",
    day: "2-digit",
  }).format(new Date(renewalDate.setFullYear(renewalDate.getFullYear() + 1)));

  return (
    // wrap a check around this to ensure the user is on a monthly plan and that the upgrade option is available
    <>
      <Grid container sx={v4GridContainerWidthSx}>
        <Grid size={{ xs: 12, sm: 12 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={breadcrumbContainerSx}>
            <ActiveBreadcrumbLink to="/myaccount/overview">
              Overview
            </ActiveBreadcrumbLink>
            <Typography sx={disabledBreadcrumbSx}>Annual Upgrade</Typography>
          </Breadcrumbs>
        </Grid>

        {membershipDetails && !upgradePreviewDetails && (
          <Container maxWidth="sm">
            <Grid container sx={v4GridContainerWidthSx}>
              <Grid size={{ xs: 12 }} align="center">
                <Typography variant="h2" sx={myAccountSubTitleSx}>
                  Upgrade Not Available
                </Typography>
                <Typography variant="body1" sx={bodyTextSx}>
                  The Annual Upgrade is not available. Return to &apos;My
                  Account&apos;.
                </Typography>
                <NoUnderlineLink to="/myaccount/overview">
                  <Button size="large" sx={buttonColourRestoreSx}>
                    My Account
                  </Button>
                </NoUnderlineLink>
              </Grid>
            </Grid>
          </Container>
        )}
        {overview &&
          overview.doors.annualUpgradeOpen &&
          membershipDetails &&
          membershipDetails.hasStripeCustomer &&
          membershipDetails.productId.includes("monthly") && (
            <>
              {updating && polling && (
                <Background style={{ textAlign: "center" }}>
                  <CircularProgress color="primary" />
                  <Typography
                    variant="body1"
                    sx={bodyTextSx}
                    style={{ marginTop: "12px" }}
                  >
                    <b>Updating...</b>
                    <br />
                    Do not refresh the page or close your browser
                  </Typography>
                </Background>
              )}

              {membershipDetails &&
                upgradePreviewDetails &&
                !updating &&
                !polling && (
                  <Container maxWidth="sm">
                    {!annualUpgradeSuccess && !annualUpgradeFailure && (
                      <>
                        <Grid container sx={v4GridContainerWidthSx}>
                          <Grid size={{ xs: 12 }}>
                            <Typography variant="h2" sx={myAccountSubTitleSx}>
                              Upgrade to Annual Plan
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 5 }}>
                            <Typography sx={currentPlanLabelSx}>
                              Current Plan
                            </Typography>
                            <Box sx={membershipPlanContainerInnerDisabledSx}>
                              <Typography sx={productTitleSx}>
                                Monthly
                                <br />
                                Membership
                              </Typography>
                              <br />
                              <Typography sx={productCostSx}>
                                {CurrencySymbol({
                                  currency: membershipDetails.currency,
                                })}
                                {membershipDetails.subscription.cost / 100}
                                <br />
                                <span>per month</span>
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid size={{ xs: 2 }}>
                            <Box sx={membershipPlanContainerMidSx}>
                              <ArrowForwardIcon />
                            </Box>
                          </Grid>
                          <Grid size={{ xs: 5 }}>
                            <Typography sx={promoLabelSx}>Save 30%</Typography>
                            <Box sx={membershipPlanContainerInnerSx}>
                              <Typography sx={productTitleSx}>
                                Annual
                                <br />
                                Membership
                              </Typography>
                              <br />
                              {subscriptionProducts.results
                                .filter(
                                  (subscription) =>
                                    subscription.currency === currency &&
                                    subscription.cost > 0 &&
                                    subscription.entitlementPeriodUnit ===
                                      "Year",
                                )
                                .map((subscription, index) => (
                                  <>
                                    <Typography sx={productCostSx}>
                                      {CurrencySymbol({
                                        currency: membershipDetails.currency,
                                      })}
                                      {subscription.cost}
                                      <br />
                                      <span>per year</span>
                                    </Typography>
                                  </>
                                ))}
                            </Box>
                          </Grid>

                          <Grid size={{ xs: 12 }}>
                            <Divider sx={dividerSx} />
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                            <Typography variant="body2" sx={bodyTextSx}>
                              By upgrading to an Annual FarmFLiX Membership,
                              your billing will be adjusted. As you are
                              currently in a plan, we have calculated the
                              difference of{" "}
                              <b>
                                {CurrencySymbol({
                                  currency: upgradePreviewDetails.currency,
                                })}
                                {(
                                  upgradePreviewDetails.amount_due / 100
                                ).toFixed(2)}
                              </b>{" "}
                              to upgrade to the Annual Plan.{" "}
                            </Typography>
                            {membershipDetails.defaultPaymentMethod ? (
                              <>
                                <Typography variant="body2" sx={bodyTextSx}>
                                  If you wish to proceed, we will attempt to
                                  bill{" "}
                                  <b>
                                    {" "}
                                    {CurrencySymbol({
                                      currency: upgradePreviewDetails.currency,
                                    })}
                                    {(
                                      upgradePreviewDetails.amount_due / 100
                                    ).toFixed(2)}
                                  </b>{" "}
                                  to your current saved payment method:
                                </Typography>
                                <Box sx={cardContainerSx}>
                                  <Box>
                                    {membershipDetails.defaultPaymentMethod
                                      .cardBrand === "visa" && (
                                      <CardBrandImg
                                        alt={
                                          membershipDetails.defaultPaymentMethod
                                            .cardBrand
                                        }
                                        src={visa}
                                      />
                                    )}
                                    {membershipDetails.defaultPaymentMethod
                                      .cardBrand === "mastercard" && (
                                      <CardBrandImg
                                        alt={
                                          membershipDetails.defaultPaymentMethod
                                            .cardBrand
                                        }
                                        src={mastercard}
                                      />
                                    )}
                                    {membershipDetails.defaultPaymentMethod
                                      .cardBrand === "amex" && (
                                      <CardBrandImg
                                        alt={
                                          membershipDetails.defaultPaymentMethod
                                            .cardBrand
                                        }
                                        src={amex}
                                      />
                                    )}
                                    {membershipDetails.defaultPaymentMethod
                                      .cardBrand === "discover" && (
                                      <CardBrandImg
                                        alt={
                                          membershipDetails.defaultPaymentMethod
                                            .cardBrand
                                        }
                                        src={discover}
                                      />
                                    )}
                                  </Box>
                                  <Box>
                                    <Typography
                                      variant="body1"
                                      sx={bodyTextSmlSx}
                                    >
                                      <span>
                                        **** **** ****{" "}
                                        {
                                          membershipDetails.defaultPaymentMethod
                                            .last4
                                        }
                                      </span>
                                      <br />
                                      <CardExpirySpan>
                                        Exp:{" "}
                                        {
                                          membershipDetails.defaultPaymentMethod
                                            .exp.month
                                        }{" "}
                                        /{" "}
                                        {
                                          membershipDetails.defaultPaymentMethod
                                            .exp.year
                                        }
                                      </CardExpirySpan>
                                    </Typography>
                                  </Box>
                                </Box>
                              </>
                            ) : (
                              <Typography variant="body1" sx={bodyTextSx}>
                                You have no saved payment method. Set up now.
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                        <Divider sx={dividerSx} />
                        <Grid container sx={v4GridContainerWidthSx}>
                          <Grid
                            size={{ xs: 12, sm: 12, md: 12 }}
                            sx={confirmContainerSx}
                          >
                            <Box sx={{ display: "flex" }}>
                              <Box>
                                {/* ♦ v9 removed `inputProps` from SwitchBase
                                    (Radio/Checkbox/Switch) — it now lands in
                                    `...other` and is spread onto the ROOT span,
                                    silently dropping the input's `name` and
                                    aria-label. The replacement is
                                    `slotProps.input`, which reaches the same
                                    <input> v4's `inputProps` did. */}
                                <Checkbox
                                  color="primary"
                                  style={{ color: "#99ca3c" }}
                                  onChange={handleTermsChecked}
                                  slotProps={{
                                    input: {
                                      name: "terms",
                                      "aria-label": "primary checkbox",
                                    },
                                  }}
                                />
                              </Box>
                              <Box>
                                <Typography variant="body2" sx={termsLabelSx}>
                                  I agree to FarmFLiX&apos;s{" "}
                                  <Link to="/terms">Terms and Conditions</Link>{" "}
                                  and{" "}
                                  <Link to="/privacy-policy">
                                    Privacy Policy
                                  </Link>
                                  .
                                  <>
                                    Your next membership payment will be taken
                                    on <b>{renewalDate}</b>. FarmFLiX will
                                    automatically track and charge your
                                    membership fee{" "}
                                    <b>
                                      (
                                      {subscriptionProducts.results
                                        .filter(
                                          (subscription) =>
                                            subscription.currency ===
                                              currency &&
                                            subscription.cost > 0 &&
                                            subscription.entitlementPeriodUnit ===
                                              "Year",
                                        )
                                        .map((subscription, index) => (
                                          <>
                                            {CurrencySymbol({
                                              currency:
                                                membershipDetails.currency,
                                            })}
                                            {subscription.cost}
                                          </>
                                        ))}
                                      /Year)
                                    </b>{" "}
                                    until you cancel your membership.
                                  </>
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={orphanGridItemFullSx} align="center">
                              <Button
                                size="large"
                                style={{ marginTop: "18px" }}
                                onClick={upgradeMembership}
                                disabled={!submittingButton}
                                sx={buttonColourRestoreSx}
                              >
                                Upgrade to Annual
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>
                      </>
                    )}
                    {annualUpgradeSuccess && (
                      <>
                        <Grid container sx={v4GridContainerWidthSx}>
                          <Grid size={{ xs: 12 }}>
                            <Box sx={responseBoxSx}>
                              <CheckCircleIcon
                                sx={responseIconSx}
                                color="primary"
                              />
                              <Typography
                                component="h2"
                                variant="h2"
                                sx={myAccountSubTitleSx}
                              >
                                Membership Upgraded!
                              </Typography>
                              <Typography
                                variant="h3"
                                sx={myAccountThirdTitleSx}
                                style={{ textAlign: "center" }}
                              >
                                Payment Successfully Processed!
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={bodyTextSx}
                                style={{ textAlign: "center" }}
                              >
                                Thank you for your payment. Your FarmFLiX
                                Membership has been upgraded.
                              </Typography>
                              <Link
                                to="/videos"
                                style={{ textDecoration: "none" }}
                              >
                                <Button size="large" sx={buttonColourRestoreSx}>
                                  Watch Now
                                </Button>
                              </Link>
                            </Box>
                          </Grid>
                        </Grid>
                      </>
                    )}
                    {annualUpgradeFailure && (
                      <>
                        <Grid container sx={v4GridContainerWidthSx}>
                          <Grid size={{ xs: 12 }}>
                            <Box sx={responseBoxSx}>
                              <CheckCircleIcon
                                sx={responseIconSx}
                                color="primary"
                              />
                              <Typography
                                component="h2"
                                variant="h2"
                                sx={myAccountSubTitleSx}
                              >
                                Payment Failed
                              </Typography>
                              <Typography
                                variant="h3"
                                sx={myAccountThirdTitleSx}
                                style={{ textAlign: "center" }}
                              >
                                We were unable to charge your card for your
                                FarmFLiX Membership.
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={bodyTextSx}
                                style={{ textAlign: "center" }}
                              >
                                Please get in touch with FarmFLiX Support.
                              </Typography>
                              <Link
                                to="/myaccount/overview"
                                style={{ textDecoration: "none" }}
                              >
                                <Button size="large" sx={buttonColourRestoreSx}>
                                  My Account
                                </Button>
                              </Link>
                            </Box>
                          </Grid>
                        </Grid>
                      </>
                    )}
                  </Container>
                )}
              {memberShipDetailsLoading && (
                <Background style={{ textAlign: "center" }}>
                  <CircularProgress color="primary" />
                  <Typography
                    variant="body1"
                    sx={bodyTextSx}
                    style={{ marginTop: "12px" }}
                  >
                    Loading...
                  </Typography>
                </Background>
              )}
            </>
          )}
        {overview && !overview.doors.annualUpgradeOpen && (
          <>
            <Container maxWidth="sm">
              <Grid container sx={v4GridContainerWidthSx}>
                <Grid size={{ xs: 12 }} align="center">
                  <Typography variant="h2" sx={myAccountSubTitleSx}>
                    Upgrade Not Available
                  </Typography>
                  <Typography variant="body1" sx={bodyTextSx}>
                    The Annual Upgrade is no longer available. Return to
                    &apos;My Account&apos;.
                  </Typography>
                  <NoUnderlineLink to="/myaccount/overview">
                    <Button size="large" sx={buttonColourRestoreSx}>
                      My Account
                    </Button>
                  </NoUnderlineLink>
                </Grid>
              </Grid>
            </Container>
          </>
        )}
      </Grid>
    </>
  );
}
